import React, { memo, useCallback, useMemo, useEffect, useRef, useState } from 'react';

// Memoized IconText component (unchanged)
const IconText = memo(({ link, title, icon, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, triggerOnce: true }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={elementRef}
      className={`icon-text-wrapper ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
      style={{ animationDelay: `${delay}s` }}
    >
      <a href={link} className="icon-text group">
        <div className="icon-text-img">
          <div className="icon-hover-effect" />
          <img
            className="icon-image"
            src={icon}
            alt={title}
            loading="lazy"
            decoding="async"
            width="64"
            height="64"
          />
        </div>
        <div className="icon-text-text">
          <h4 className="icon-title">
            {title}
          </h4>
        </div>
      </a>
    </div>
  );
});

IconText.displayName = 'IconText';

// Main component
const ServicesSection = ({ trans = (key) => key, onMaintenanceClick }) => {
  const [isAnimate, setIsAnimate] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsAnimate(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleServiceOrder = useCallback((e) => {
    if (e) e.preventDefault();
    if (typeof onMaintenanceClick === 'function') {
      onMaintenanceClick({ showPopup: true });
    }
  }, [onMaintenanceClick]);

  // Safe translation function (unchanged)
  const t = useCallback((key) => {
    if (typeof trans === 'function') {
      return trans(key);
    }
    const fallbacks = {
      'home:services': 'الخدمات',
      'home:eparts': 'قطع الغيار',
      'home:finance': 'التمويل',
      'home:vehicles-shop': 'متجر السيارات',
      'home:offers': 'العروض',
      'home:maintenance': 'الصيانة',
      'home:premium': 'خدمات فاخرة',
      'home:click_schedule': 'اضغط للجدولة ←'
    };
    return fallbacks[key] || key;
  }, [trans]);

  // Service items configuration (unchanged)
  const serviceItems = useMemo(() => [
    { link: '/finance', title: t('home:finance'), icon: '/assets/icons/services/fin.svg', delay: 0.2 },
    { link: '/VehicleOnline', title: t('home:vehicles-shop'), icon: '/assets/icons/services/shop.svg', delay: 0.3 },
    { link: '/offers', title: t('home:offers'), icon: '/assets/icons/services/offer.svg', delay: 0.4 },
  ], [t]);

  return (
    <>
      <style>{`
        /* Services Section Styles */
        .services-section {
          position: relative;
          width: 100%;
          padding: 0 1rem;
          margin-bottom: 15px;
          background: transparent;
        }

        .services-container {
          background: #ffffff;
          border-radius: 2rem;
          box-shadow: 0 40px 100px -20px rgba(0, 0, 0, 0.05), 
                      0 20px 40px -15px rgba(0, 0, 0, 0.03);
          padding: 2rem 1.5rem;
          max-width: 900px;
          margin: 0 auto;
          position: relative;
          z-index: 10;
          border: 1px solid rgba(0, 0, 0, 0.02);
          transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
        }

        @media (min-width: 768px) {
          .services-container {
            padding: 3rem 2rem;
            border-radius: 3rem;
          }
        }

        .services-container:hover {
          transform: translateY(-5px);
          box-shadow: 0 50px 120px -20px rgba(0, 0, 0, 0.08);
        }

        /* Updated: Always side by side, no wrapping, no scroll */
        .services-grid {
          display: flex;
          flex-direction: row;
          align-items: stretch;
          justify-content: space-between; /* Space items evenly */
          gap: 0.25rem; /* Reduced gap for small screens */
          margin: 0 auto;
          width: 100%;
          flex-wrap: nowrap;
          overflow: visible;
        }

        @media (min-width: 480px) {
          .services-grid {
            gap: 0.75rem;
            justify-content: center;
          }
        }

        @media (min-width: 768px) {
          .services-grid {
            gap: 1.5rem;
          }
        }

        @media (min-width: 1024px) {
          .services-grid {
            gap: 2rem;
          }
        }

        /* Utility Classes */
        .opacity-0 {
          opacity: 0;
        }

        /* Updated: Make all items flexible but fixed width on small screens */
        .icon-text-wrapper {
          flex: 1; /* Distribute space evenly */
          min-width: 0;
          opacity: 0;
          display: flex;
          justify-content: center;
        }

        /* On very small screens, ensure they still fit */
        @media (max-width: 480px) {
          .services-container {
            padding: 1rem 0.75rem !important; /* Compact padding on mobile */
          }
        }

        /* Icon Text Styles */
        .icon-text {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          color: inherit;
          transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
          cursor: pointer;
          background: transparent;
          padding: 0.5rem 0.25rem;
          height: 100%;
          width: 100%;
          min-height: 80px;
          position: relative;
          z-index: 1;
        }

        @media (min-width: 768px) {
          .icon-text {
            padding: 1rem 1.5rem;
            min-height: 120px;
          }
        }

        .icon-text:hover {
          transform: translateY(-6px);
        }

        /* Icon Image Container */
        .icon-text-img {
          position: relative;
          margin-bottom: 0.75rem;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        @media (min-width: 768px) {
           .icon-text-img {
             margin-bottom: 1rem;
           }
        }

        .icon-hover-effect {
          position: absolute;
          inset: -8px;
          background-color: var(--primary, #00b362);
          opacity: 0;
          border-radius: 9999px;
          transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .icon-text:hover .icon-hover-effect {
          opacity: 0.1;
        }

        /* Icon Image */
        .icon-image {
          width: 48px;
          height: 48px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
          transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
          object-fit: contain;
        }

        @media (max-width: 767px) {
          .icon-image {
            width: 64px;
            height: 64px;
          }
          .icon-text-img {
            width: 72px;
            height: 72px;
            margin-bottom: 0.25rem;
          }
        }

        @media (min-width: 768px) {
          .icon-image {
            width: 56px;
            height: 56px;
          }
        }

        .icon-text:hover .icon-image {
          transform: scale(1.15) rotate(2deg);
        }

        /* Icon Title */
        .icon-title {
          font-size: 1.05rem;
          font-weight: 700;
          margin-bottom: 0;
          margin-top: 0.25rem;
          transition: color 0.3s ease;
          text-align: center;
          white-space: nowrap;
        }

        @media (max-width: 767px) {
          .icon-title {
            font-size: 1.25rem;
          }
        }

        @media (min-width: 768px) {
          .icon-title {
            font-size: 1.125rem;
          }
        }

        .icon-text:hover .icon-title {
          color: var(--primary, #00b362);
        }

        /* Maintenance Card Specific */
        .maintenance-effect {
          background-color: var(--primary, #00b362) !important;
        }

        .maintenance-label {
          font-size: 0.75rem;
          color: #9ca3af;
          opacity: 0;
          transition: all 0.3s ease; 
          display: block;
          position: absolute;
          bottom: 0.25rem;
          left: 0;
          right: 0;
          text-align: center;
          pointer-events: none;
          font-weight: 500;
        }

        .maintenance-item:hover .maintenance-label {
          opacity: 1;
        }

        /* Adjust maintenance item to accommodate label */
        .maintenance-item {
          padding-bottom: 1.5rem;
        }

        /* Animations (unchanged) */
        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInDown {
          0% {
            opacity: 0;
            transform: translateY(-30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fadeInUp 0.7s cubic-bezier(0.21, 1.11, 0.37, 1.11) forwards;
        }

        .animate-fade-in-down {
          animation: fadeInDown 0.7s cubic-bezier(0.21, 1.11, 0.37, 1.11) forwards;
        }

        /* Dark Mode Support (unchanged) */
        body.dark .services-container {
          background: #18181b;
          border-color: rgba(255, 255, 255, 0.05);
          box-shadow: 0 40px 100px -20px rgba(0, 0, 0, 0.2);
        }
        body.dark .services-container:hover {
          box-shadow: 0 50px 120px -20px rgba(0, 0, 0, 0.3);
        }
        body.dark .icon-title {
          color: #f4f4f5;
        }
        body.dark .icon-text:hover .icon-title {
          color: var(--primary, #00b362);
        }
        body.dark .maintenance-label {
          color: #a1a1aa;
        }
      `}</style>

      <section className="services-section">
        <div
          ref={containerRef}
          className={`services-container ${isAnimate ? 'animate-fade-in-up' : 'opacity-0'}`}
        >
          <div className="services-grid">
            {serviceItems.map((item) => (
              <IconText
                key={item.link}
                link={item.link}
                title={item.title}
                icon={item.icon}
                delay={item.delay}
              />
            ))}

            {/* Maintenance card with special styling */}
            <div className={`icon-text-wrapper ${isAnimate ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.5s' }}>
              <a
                href="#"
                className="icon-text group maintenance-item"
                onClick={handleServiceOrder}
                onKeyPress={(e) => e.key === 'Enter' && handleServiceOrder(e)}
                role="button"
                tabIndex={0}
                aria-label={t('home:maintenance')}
              >
                <div className="icon-text-img">
                  <div className="icon-hover-effect maintenance-effect" />
                  <img
                    className="icon-image"
                    src="/assets/icons/services/mant.svg"
                    alt={t('home:maintenance')}
                    loading="lazy"
                    decoding="async"
                    width="64"
                    height="64"
                  />
                </div>
                <div className="icon-text-text">
                  <h4 className="icon-title">
                    {t('home:maintenance')}
                  </h4>
                </div>
                <span className="maintenance-label">
                  {t('home:click_schedule')}
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default memo(ServicesSection);